import React, { useEffect, useState } from "react";
import axios from "axios";
import { Toaster } from "sonner";
import { Sparkles, Loader2, Lock } from "lucide-react";
import Today from "./Today";

/**
 * Standalone housekeeping page for the "governante": no admin access, no
 * navigation bar, no back arrow. PIN is stored in sessionStorage so it
 * survives page reloads within the tab. The PIN is validated once at unlock
 * time and re-verified on each API call (backend accepts X-HK-PIN header).
 *
 * Route: /pulizie (public)
 */
function Header() {
  return (
    <div className="bg-[#3A1B18] text-[#F7EBDF] rounded-b-3xl px-4 pt-3 pb-6 shadow-md relative overflow-hidden">
      <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-[#C5A059]/10 pointer-events-none" />
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-[#F7EBDF] flex items-center justify-center shadow-md shrink-0" data-testid="hk-header-logo">
            <Sparkles className="h-4 w-4 text-[#4A1C17]" />
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-lg sm:text-xl font-bold tracking-wide leading-tight truncate" data-testid="hk-header-title">
              Gestione Pulizie
            </h1>
            <p className="text-sm text-[#C5A059] leading-tight italic truncate">Lavori del giorno</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function PinGate({ onUnlock }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr("");
    setBusy(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/hk/verify-pin`, { pin: pin.trim() });
      sessionStorage.setItem("hk_pin", pin.trim());
      onUnlock();
    } catch (e) {
      setErr(e?.response?.data?.detail || "PIN non valido");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#4A1C17] px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#F7EBDF] rounded-2xl shadow-2xl p-6 sm:p-8" data-testid="hk-pin-gate">
        <div className="flex flex-col items-center mb-4">
          <div className="h-14 w-14 rounded-full bg-[#4A1C17] flex items-center justify-center mb-3">
            <Lock className="h-6 w-6 text-[#F7EBDF]" />
          </div>
          <h1 className="font-serif text-xl font-bold text-[#2B1410]">Gestione Pulizie</h1>
          <p className="text-xs text-[#7C5A4E] italic mt-1">Inserisci il PIN governante</p>
        </div>
        <label className="text-[10px] uppercase tracking-widest font-semibold text-[#7C5A4E] block mb-1">PIN</label>
        <input
          type="tel"
          inputMode="numeric"
          autoFocus
          maxLength={8}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          className="w-full text-center text-2xl font-mono tracking-widest bg-white border-2 border-[#E8DCC8] rounded-lg py-3 focus:outline-none focus:border-[#4A1C17]"
          data-testid="hk-pin-input"
          placeholder="••••"
        />
        {err && <p className="text-xs text-red-600 mt-2 text-center" data-testid="hk-pin-error">{err}</p>}
        <button
          type="submit"
          disabled={busy || pin.length < 4}
          className="w-full mt-4 rounded-lg bg-[#4A1C17] hover:bg-[#2B1410] text-[#F7EBDF] py-3 font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="hk-pin-submit"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Sblocca
        </button>
      </form>
    </div>
  );
}

export default function HousekeeperOnlyPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Re-verify the stored PIN so a rotated PIN kicks a stale tab out.
    const pin = sessionStorage.getItem("hk_pin");
    if (!pin) { setChecking(false); return; }
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/hk/verify-pin`, { pin })
      .then(() => setUnlocked(true))
      .catch(() => sessionStorage.removeItem("hk_pin"))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7ebdf]">
        <Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" />
      </div>
    );
  }
  if (!unlocked) return <PinGate onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-[#F7EBDF]">
      <Header />
      <main className="pb-12">
        <Today />
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
}
